import React, { useState, useEffect } from "react";
import { getAPIURL, getVendorID, getVendorSettings } from "../../services/helper";
import http from "../../services/http";
import DataTable from "react-data-table-component";
import {
  Spinner,
  Container,
  Card,
  Input,
  Button,
  CardBody,
  CardTitle,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { FaStore, FaLaptop, FaDollarSign, FaChartLine } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { openView } from "../../ReduxToolkit/Reducers/viewReducer";
import classnames from "classnames";

const SalesReportByPlatform = () => {
  const api_url = getAPIURL();
  const vendorId = getVendorID();
  const dispatch = useDispatch();
  const [data, setData] = useState({ summary: {}, platforms: {} });
  const [activeTab, setActiveTab] = useState("summary");
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0], // Last month
    end: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchSalesReport();
  }, []);

  const printReport = () => {
    const vendor_email = getVendorSettings("email");
    const vendor_store_name = getVendorSettings("name");
    const vendor_address = getVendorSettings("vendor_contact_address");
    const vendor_phone = getVendorSettings("vendor_contact_phone");
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Sales Report by Platform</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h2 { margin: 0; }
            .header p { margin: 0; }
            .header h3 { margin-top: 20px; margin-bottom: 10px; }
            .header hr { margin-top: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .summary { margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>${vendor_store_name}</h2>
            <p>${vendor_address}</p>
            <p>${vendor_phone} | ${vendor_email}</p>
            <h3>Sales Report by Platform</h3>
            <hr>
          </div>
          <div class="summary">
            <p><strong>Date Range:</strong> ${dateRange.start} to ${dateRange.end}</p>
            <p><strong>Total Sales:</strong> ${data.summary.total_sales || 0}</p>
            <p><strong>Total Revenue:</strong> $${(data.summary.total_amount || 0).toFixed(2)}</p>
            <p><strong>POS Sales:</strong> $${(data.platforms.POS?.total_amount || 0).toFixed(2)}</p>
            <p><strong>Tealbo Sales:</strong> $${(data.platforms.Tealbo?.total_amount || 0).toFixed(2)}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Platform</th>
                <th>Total Sales</th>
                <th>Total Transactions</th>
                <th>Total Revenue</th>
                <th>Tax Collected</th>
                <th>Total Discount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>POS</td>
                <td>${data.platforms.POS?.total_sales || 0}</td>
                <td>${data.platforms.POS?.total_transactions || 0}</td>
                <td>$${(data.platforms.POS?.total_amount || 0).toFixed(2)}</td>
                <td>$${(data.platforms.POS?.total_tax || 0).toFixed(2)}</td>
                <td>$${(data.platforms.POS?.total_discount || 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td>Tealbo</td>
                <td>${data.platforms.Tealbo?.total_sales || 0}</td>
                <td>${data.platforms.Tealbo?.total_transactions || 0}</td>
                <td>$${(data.platforms.Tealbo?.total_amount || 0).toFixed(2)}</td>
                <td>$${(data.platforms.Tealbo?.total_tax || 0).toFixed(2)}</td>
                <td>$${(data.platforms.Tealbo?.total_discount || 0).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const fetchSalesReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ vendor_id: vendorId });
      if (dateRange.start) params.append("start_date", dateRange.start);
      if (dateRange.end) params.append("end_date", dateRange.end);

      const { data } = await http.get(
        `${api_url}/reports/sales/platform?${params.toString()}`
      );
      setData(data);
    } catch (error) {
      console.error("Error fetching sales report:", error);
    }
    setLoading(false);
  };

  return (
    <div className="page-body">
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="text-center flex-grow-1">Sales Report by Platform</h2>
        </div>

        {/* Filters */}
        <div className="d-flex mb-3 gap-2">
          <Input
            type="date"
            value={dateRange.start}
            onChange={(e) =>
              setDateRange({ ...dateRange, start: e.target.value })
            }
          />
          <Input
            type="date"
            value={dateRange.end}
            onChange={(e) =>
              setDateRange({ ...dateRange, end: e.target.value })
            }
          />
          <Button color="primary" onClick={fetchSalesReport}>
            Filter
          </Button>
          <Button
            color="info"
            onClick={() => {
              setDateRange({
                start: new Date(new Date().setMonth(new Date().getMonth() - 1))
                  .toISOString()
                  .split("T")[0],
                end: new Date().toISOString().split("T")[0],
              });
              fetchSalesReport();
            }}
          >
            Reset
          </Button>
        </div>

        {/* Tabs */}
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "summary" })}
              onClick={() => setActiveTab("summary")}
              style={{ cursor: "pointer" }}
            >
              Summary
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "listing" })}
              onClick={() => setActiveTab("listing")}
              style={{ cursor: "pointer" }}
            >
              Listing
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="summary">
            {loading ? (
              <Spinner color="primary" />
            ) : (
              <Row className="mb-4">
                <Col md="3">
                  <Card className="p-3 shadow-sm text-center">
                    <CardBody>
                      <FaChartLine size={30} className="text-info mb-2" />
                      <CardTitle tag="h5">Total Sales</CardTitle>
                      <h4>{data.summary.total_sales || 0}</h4>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="3">
                  <Card className="p-3 shadow-sm text-center">
                    <CardBody>
                      <FaDollarSign size={30} className="text-success mb-2" />
                      <CardTitle tag="h5">Total Revenue</CardTitle>
                      <h4>${(data.summary.total_amount || 0).toFixed(2)}</h4>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="3">
                  <Card className="p-3 shadow-sm text-center">
                    <CardBody>
                      <FaStore size={30} className="text-primary mb-2" />
                      <CardTitle tag="h5">POS Sales</CardTitle>
                      <h4>${(data.platforms.POS?.total_amount || 0).toFixed(2)}</h4>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="3">
                  <Card className="p-3 shadow-sm text-center">
                    <CardBody>
                      <FaLaptop size={30} className="text-warning mb-2" />
                      <CardTitle tag="h5">Tealbo Sales</CardTitle>
                      <h4>${(data.platforms.Tealbo?.total_amount || 0).toFixed(2)}</h4>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
          </TabPane>

          <TabPane tabId="listing">
            {loading ? (
              <Spinner color="primary" />
            ) : (
              <Card className="p-3 shadow-sm">
                <h4 className="mb-3">Sales by Platform</h4>
                <DataTable
                  columns={[
                    {
                      name: "Platform",
                      selector: (row) => row.platform,
                      sortable: true,
                    },
                    {
                      name: "Total Sales",
                      selector: (row) => row.total_sales || 0,
                      sortable: true,
                    },
                    {
                      name: "Total Transactions",
                      selector: (row) => row.total_transactions || 0,
                      sortable: true,
                    },
                    {
                      name: "Total Revenue",
                      selector: (row) => `$${(row.total_amount || 0).toFixed(2)}`,
                      sortable: true,
                    },
                    {
                      name: "Tax Collected",
                      selector: (row) => `$${(row.total_tax || 0).toFixed(2)}`,
                      sortable: true,
                    },
                    {
                      name: "Total Discount",
                      selector: (row) => `$${(row.total_discount || 0).toFixed(2)}`,
                      sortable: true,
                    },
                  ]}
                  data={[
                    { platform: "POS", ...data.platforms.POS },
                    { platform: "Tealbo", ...data.platforms.Tealbo },
                  ]}
                  pagination
                  striped
                />
              </Card>
            )}
          </TabPane>
        </TabContent>
        
        <div className="text-center mt-4 mb-4">
          <Button color="success" onClick={printReport}>
            Print Report
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default SalesReportByPlatform;
